// AssignDeviceDialog
// ------------------
// Lightweight dialog used from the per-company Devices tab to link an
// EXISTING device into this company. There's deliberately no create path —
// new devices are added on the global /devices page first, then assigned
// here.
//
// Target picker is a DeviceSearchAutocomplete: substring on IMEI/name/model
// or `/pattern/` regex on IMEI. Once a device is selected, the dialog
// classifies it (already here / standalone / other company) and offers the
// appropriate action.

import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Link as MuiLink,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  assignDeviceToCompany,
  fetchCompanyDevices,
} from "./companyDevicesSlice";
import DeviceSearchAutocomplete from "./DeviceSearchAutocomplete";
import DangerConfirmField, {
  isDangerCodeValid,
} from "../../components/common/DangerConfirmField";

export default function AssignDeviceDialog({ company, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [picked, setPicked] = useState(null);
  const [securityCode, setSecurityCode] = useState("");
  const [assigning, setAssigning] = useState(false);

  // Classify the picked device relative to the current company.
  const pickedCompanyId = picked?.company?._id || null;
  const isSameCompany = picked && pickedCompanyId === company._id;
  const isStandalone = Boolean(picked) && !pickedCompanyId;
  const isOtherCompany =
    Boolean(pickedCompanyId) && pickedCompanyId !== company._id;
  const needsSecurityCode = isOtherCompany;

  const canAssign =
    Boolean(picked) &&
    !isSameCompany &&
    (isStandalone || (isOtherCompany && isDangerCodeValid(securityCode))) &&
    !assigning;

  const performAssign = async () => {
    if (!canAssign) return;
    setAssigning(true);
    const res = await dispatch(
      assignDeviceToCompany({ id: picked._id, companyId: company._id })
    );
    setAssigning(false);
    if (assignDeviceToCompany.fulfilled.match(res)) {
      toast.success(
        `${picked.device_name || picked.IMEI} assigned to ${company.name}`
      );
      dispatch(fetchCompanyDevices({ companyId: company._id }));
      onClose();
    } else {
      toast.error(res.payload || "Assign failed");
    }
  };

  const goToDevices = () => {
    onClose();
    navigate("/devices");
  };

  return (
    <Dialog
      open
      onClose={assigning ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          position: "relative",
          px: 2,
          pt: 2.5,
          pb: 2,
          background:
            "linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)",
          color: "common.white",
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={assigning}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "common.white",
            backgroundColor: "rgba(255,255,255,0.15)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              bgcolor: "rgba(255,255,255,0.22)",
              border: "2px solid rgba(255,255,255,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LinkIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                letterSpacing: "-0.01em",
                lineHeight: 1.15,
              }}
            >
              Assign Device
            </Typography>
            <Typography
              sx={{ fontSize: "0.75rem", opacity: 0.92, mt: 0.25 }}
            >
              Link an existing device to <b>{company.name}</b>
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        <DeviceSearchAutocomplete
          value={picked}
          onChange={(d) => {
            setPicked(d);
            setSecurityCode("");
          }}
          autoFocus
          disabled={assigning}
          helperText="Search by IMEI, name, or model. Use /…/ for regex on IMEI."
        />

        {/* State alerts */}
        {isSameCompany && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            <Typography variant="caption">
              <b>{picked.IMEI}</b> is already in this company.
            </Typography>
          </Alert>
        )}
        {isStandalone && (
          <Alert severity="success" sx={{ mt: 1.5 }}>
            <Typography variant="caption">
              Found standalone device{" "}
              <b>{picked.device_name || picked.IMEI}</b>. Ready to assign.
            </Typography>
          </Alert>
        )}
        {isOtherCompany && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            <Typography variant="caption">
              <b>{picked.IMEI}</b> currently belongs to{" "}
              <b>{picked.company?.name || "another company"}</b>. Assigning
              here will remove it from there (admins + device-order lists are
              cleared). Enter the security code to confirm.
            </Typography>
          </Alert>
        )}

        {!picked && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1.5 }}
          >
            Can't find the device?{" "}
            <MuiLink
              component="button"
              onClick={goToDevices}
              sx={{ fontSize: "inherit" }}
            >
              Add it on the Devices page first
            </MuiLink>
            .
          </Typography>
        )}

        {needsSecurityCode && (
          <DangerConfirmField
            value={securityCode}
            onChange={setSecurityCode}
            disabled={assigning}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={assigning}
          sx={{ borderRadius: "8px", flex: { xs: 1, sm: "0 0 auto" } }}
        >
          Cancel
        </Button>
        <Button
          onClick={performAssign}
          variant="contained"
          color={isOtherCompany ? "warning" : "primary"}
          disabled={!canAssign}
          startIcon={
            assigning ? <CircularProgress size={14} color="inherit" /> : null
          }
          sx={{
            borderRadius: "8px",
            flex: { xs: 1, sm: "0 0 auto" },
            boxShadow: "0 4px 10px -4px rgba(13,148,136,0.55)",
          }}
        >
          {assigning ? "Assigning…" : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
